import { motion } from "framer-motion";

const STORY_STEPS = [
  {
    num: "01",
    title: "The Problem We Lived",
    body: "We were real estate investors frustrated by the same broken process — spreadsheets everywhere, gut-feel decisions, and scrambling to put together DSCR reports the night before a lender meeting. We knew there had to be a better way.",
    accent: "blue",
  },
  {
    num: "02",
    title: "The Vision We Chose",
    body: "Every investment decision should be backed by real data. Net worth, portfolio performance, lender-ready reports — all in one place, updated automatically, with AI that tells you what it means and when to act.",
    accent: "indigo",
  },
  {
    num: "03",
    title: "The Platform We Built",
    body: "VaWeNa — WealthScope + PortfolioIQ — is the intelligent investor OS we wish we'd had. Two focused modules that together give you complete financial clarity, from deal readiness to portfolio performance to tax reports.",
    accent: "cyan",
  },
];

const accentMap: Record<string, { pill: string; border: string; num: string; glow: string }> = {
  blue:   { pill: "bg-blue-500/10 text-blue-400 border-blue-500/20",   border: "border-blue-500/30",   num: "from-blue-400 to-blue-600",   glow: "bg-blue-500/10" },
  indigo: { pill: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20", border: "border-indigo-500/30", num: "from-indigo-400 to-indigo-600", glow: "bg-indigo-500/10" },
  cyan:   { pill: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",   border: "border-cyan-500/30",   num: "from-cyan-400 to-cyan-600",   glow: "bg-cyan-500/10" },
};

export function StorySection() {
  return (
    <section className="px-4 sm:px-6 py-20 mx-auto max-w-[1100px]">
      {/* Header */}
      <div className="text-center mb-14">
        <div className="inline-flex px-3 py-1.5 text-xs font-bold tracking-widest dark:text-blue-400 text-blue-700 uppercase dark:bg-blue-600/15 bg-blue-100 rounded-full mb-4">
          Our Story
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold dark:text-white text-gray-900 mb-3">
          Built by frustrated investors,{" "}
          <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
            for serious investors
          </span>
        </h2>
        <p className="text-base dark:text-slate-400 text-gray-500 max-w-xl mx-auto">
          We didn't build VaWeNa to win a startup competition. We built it because we needed it ourselves.
        </p>
      </div>

      {/* Steps */}
      <div className="relative">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
          {STORY_STEPS.map((step, i) => {
            const ac = accentMap[step.accent];
            return (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                className={`relative flex flex-col dark:bg-white/[0.04] bg-white border dark:border-white/[0.08] ${ac.border} rounded-2xl p-7 hover:-translate-y-1 hover:dark:bg-white/[0.07] hover:shadow-xl hover:shadow-blue-200/40 dark:hover:shadow-blue-900/20 transition-all duration-300 group`}
              >
                {/* Glow */}
                <div className={`absolute top-0 right-0 w-32 h-32 ${ac.glow} rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`} />

                {/* Number badge */}
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${ac.num} bg-opacity-20 flex items-center justify-center mb-6 mx-auto md:mx-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                  <span className="text-2xl font-extrabold text-white">{step.num}</span>
                </div>

                <h3 className="text-lg font-bold dark:text-white text-gray-900 mb-3 text-center md:text-left">
                  {step.title}
                </h3>
                <p className="text-sm dark:text-slate-400 text-gray-500 leading-relaxed text-center md:text-left">
                  {step.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Founder quote */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="mt-14 dark:bg-gradient-to-r dark:from-vw-surface dark:to-vw-bg bg-blue-50 border dark:border-blue-900/40 border-blue-100 rounded-2xl px-8 py-8 text-center"
      >
        <svg className="w-8 h-8 text-blue-400/50 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
        </svg>
        <p className="text-lg dark:text-slate-200 text-gray-700 font-medium leading-relaxed max-w-2xl mx-auto">
          "We experienced every problem you're facing — the spreadsheet chaos, the lender scramble, the gut-feel guesses. VaWeNa is the platform we wish existed when we started."
        </p>
        <p className="mt-4 text-sm dark:text-slate-500 text-gray-400 font-semibold">
          — The VaWeNa Team
        </p>
      </motion.div>
    </section>
  );
}
